import { forwardRef } from 'react'

export interface ToggleProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
  description?: string
  disabled?: boolean
}

export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
  ({ checked, onChange, label, description, disabled = false }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={`
          relative inline-flex items-center gap-3 p-3 rounded-xl border transition-all
          ${checked 
            ? 'bg-primary/20 border-primary' 
            : 'bg-background-card border-border-muted hover:border-border'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-background-elevated'}
          focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background
        `}
      >
        <div className="flex-1 text-left min-w-0">
          <div className="font-medium text-sm text-text">{label}</div>
          {description && (
            <div className="text-xs text-text-muted mt-0.5">{description}</div>
          )}
        </div>
        <div
          className={`
            relative flex-shrink-0 w-12 h-6 rounded-full transition-colors duration-200
            ${checked ? 'bg-primary' : 'bg-background-elevated'}
          `}
        >
          <div
            className={`
              absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md
              transition-transform duration-200 ease-in-out
              ${checked ? 'translate-x-6' : 'translate-x-0'}
            `}
          />
        </div>
      </button>
    )
  }
)

Toggle.displayName = 'Toggle'
